import { css, html, LitElement } from "lit";
import { property } from "lit/decorators/property.js";
import { state } from "lit/decorators/state.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { DEFAULT_BASE_URL, SUPPORTED_LANGUAGES, WIDGET_NAMES } from "../../api-sdk/constants";
import { TCart } from "../../shared/cart-contents";
import { progressBarConfigDefaults } from "../../shared/components/progress-bar";
import { enforceConfig } from "../../shared/enforce-config";
import { postFindOrCreateSubscription, postChainEligibleNonprofitsForCart, getImpact } from "../../api-sdk/v3/routes";
import { AsyncController } from "../../shared/controllers/async-controller";
import { cssReset } from "../../shared/css-reset";
import { defineCustomText, useCustomText } from "../../shared/fragments/custom-text";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { _cssVariableMap } from "../../shared/fragments/css-variable-map";
import { BeamNonprofitSelectEvent } from "../../utils/events";
import { MissingConfig } from "../../utils/beam-errors";
import "../select-subscription-nonprofit";
import "../subscription-impact";
import "../beam-partner-logos";
import "../../shared/components/progress-bar";
import { LANGUAGES } from "../../api-sdk/types";
import { partnerLogosConfigDefaults } from "../beam-partner-logos";
import { makeApiKeyHeader } from "../../utils/makeApiKeyHeader";
import { _loading } from "../../shared/fragments/loading-template";
import { localizeUserString } from "../../shared/localize";
import { TNumericId } from "../../shared/types";
import { cssCardGrid } from "../../shared/fragments/css-card-grid";
import { strings } from "./strings";

interface RequiredDataPropsWithEmail {
  email: string;
  apiKey: string;
  subscriptionId: string;
}

interface RequiredDataPropsWithPartnerUserId {
  partnerUserId: string;
  apiKey: string;
  subscriptionId: string;
}

type TRequiredDataProps = RequiredDataPropsWithEmail | RequiredDataPropsWithPartnerUserId;
export type TIAPIV3PersonalImpact = Awaited<ReturnType<typeof import("../../api-sdk/v3/routes").getImpact>>["personal"];
export type TIAPIV3EligibleNonprofits = Awaited<
  ReturnType<typeof import("../../api-sdk/v3/routes").getChainNonprofits>
>["nonprofits"];
export type TIAPIV3EligibleNonprofitConfig = Awaited<
  ReturnType<typeof import("../../api-sdk/v3/routes").getChainNonprofits>
>["config"];

const INITIAL_TILE_COUNT = 3;
const MIN_PREVIOUS_IMPACT = 0;
const MIN_PROGRESS_THRESHOLD = 1;
const MAX_PROGRESS_THRESHOLD = 98;

export class BeamSubscriptionManagement extends LitElement {
  static tagName = "beam-subscription-management";

  @property({ type: String })
  public baseUrl: string = DEFAULT_BASE_URL;

  @property({ type: String })
  public apiKey!: TRequiredDataProps["apiKey"];

  @property({ type: String })
  public subscriptionId!: TRequiredDataProps["subscriptionId"];

  @property({ type: Number, reflect: true })
  public storeId?: number; // Either storeId or postalCode, countryCode must be provided

  @property({ type: String })
  public email?: RequiredDataPropsWithEmail["email"]; // Either email or partnerUserId must be provided

  @property({ type: String })
  public partnerUserId?: RequiredDataPropsWithPartnerUserId["partnerUserId"]; // Either email or partnerUserId must be provided

  @property({ type: String })
  public postalCode?: string;

  @property({ type: String })
  public countryCode?: string;

  @property({ type: Number, reflect: true })
  public selectedNonprofitId?: TNumericId;

  @property({ type: String })
  public lang: LANGUAGES = "en";

  @property({ type: Object })
  public cart?: TCart;

  @property({ type: Boolean })
  public debug = false;

  @property({ type: Boolean })
  public draftConfig = false;

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  @state() private activeWidget: "select-subscription-nonprofit" | "subscription-impact" | null = null; // Based on /findSubscriptionByRemoteId
  @state() private nonprofitId: number | null = null; // From /findSubscriptionByRemoteId response
  @state() private beamUserId: string | null = null; // From findSubscriptionByRemoteId
  @state() private eligibleNonprofits: TIAPIV3EligibleNonprofits = [];
  @state() private showAllTiles = false;
  @state() private isMobile = window.innerWidth < 720;
  @state() private dropdownVisible = false;
  @state() private emojis = ["👏", "💪", "🙌", "✨", "🥇"];

  private subscriptionDataController = new AsyncController<typeof this.fetchSubscription>(this, this.fetchSubscription);
  private eligibleNonprofitsDataController = new AsyncController<typeof this.fetchEligibleNonprofits>(
    this,
    this.fetchEligibleNonprofits
  );
  private personalImpactDataController = new AsyncController<typeof this.fetchImpact>(this, this.fetchImpact);

  async firstUpdated() {
    if (!this.email && !this.partnerUserId) {
      throw new MissingConfig(
        ["email", "partnerUserId"],
        "One of the following properties needs to be specified: email, partnerUserId "
      );
    }
    enforceConfig<TRequiredDataProps>(["apiKey", "subscriptionId"], this);
    await this.loadWidget();
  }

  async loadWidget() {
    // In parallel:
    // (1). Fetch eligible nonprofits [storeId and chainName]
    // (2). Fetch subscription [userId and nonprofitId]
    await Promise.all([this.fetchEligibleNonprofits(), this.fetchSubscription()]);
    await this.fetchImpact();
    this.activeWidget = this.nonprofitId ? "subscription-impact" : "select-subscription-nonprofit";
  }

  async fetchEligibleNonprofits() {
    enforceConfig<TRequiredDataProps>(["apiKey", "subscriptionId"], this);
    const eligibleNonprofitResult = await postChainEligibleNonprofitsForCart({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      requestBody: {
        storeId: this.storeId || undefined,
        postalCode: this.postalCode || undefined,
        countryCode: this.countryCode || undefined,
        widgetName: WIDGET_NAMES.subscription_management,
        version: "1.0.0",
      },
    });
    this.storeId = eligibleNonprofitResult.store.id;
    this.eligibleNonprofits = eligibleNonprofitResult.nonprofits;
    this.eligibleNonprofitsDataController.data = eligibleNonprofitResult;
    return eligibleNonprofitResult;
  }

  async fetchSubscription() {
    enforceConfig<TRequiredDataProps>(["apiKey", "subscriptionId"], this);
    const subscriptionResult = await postFindOrCreateSubscription({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      requestBody: {
        remoteSubscriptionId: this.subscriptionId,
        email: this.email,
        partnerUserId: this.partnerUserId,
      },
    });

    this.beamUserId = subscriptionResult.beamUserId; // Returned in response regardless of subscription redemption
    this.nonprofitId = subscriptionResult.nonprofit?.id || null;
    return subscriptionResult;
  }

  async fetchImpact() {
    enforceConfig<TRequiredDataProps>(["apiKey"], this);

    const impactResult = await getImpact({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      queryParams: {
        beamUserId: this.beamUserId ? this.beamUserId : undefined,
        widgetName: "subscription-management",
        version: "1.0.0",
        lang: this.configLang,
      },
    });

    this.personalImpactDataController.data = impactResult;
    return impactResult;
  }

  // Used to filter the full personal impact results to
  // just ones that are displayable
  // 1. Checks "includeInPersonalImpact" for each nonprofit
  // 2. Checks if it's the currently supported nonprofit, and if it's been funded at least once
  getDisplayablePreviousImpacts() {
    const personalImpacts = this.personalImpactDataController.data?.personal;
    if (!personalImpacts || personalImpacts?.length < 1) {
      return [];
    }

    return personalImpacts.filter((impact) => {
      if (!impact.nonprofit.includeInPersonalImpact) {
        return false;
      }
      if (
        impact.nonprofit.id === this.nonprofitId &&
        (!impact.impact.goalCompletionText || impact.impact.goalCompletionText?.length < 1)
      ) {
        return false;
      }
      return true;
    });
  }

  handleSwapNonprofitButtonClick() {
    this.activeWidget = "select-subscription-nonprofit";
  }

  handleBackToSubscriptionImpactButtonClick() {
    this.activeWidget = "subscription-impact";
  }

  handleSupportAgainButtonClick(nonprofitId: TNumericId, nonprofitName: string) {
    this.activeWidget = "select-subscription-nonprofit";
    this.selectedNonprofitId = nonprofitId;

    const event = new BeamNonprofitSelectEvent({
      selectedNonprofitId: nonprofitId,
      nonprofitName: nonprofitName,
      source: WIDGET_NAMES.subscription_management,
      timestamp: new Date(),
    });
    this.dispatchEvent(event);
  }

  handleNonprofitSelect(event: Event) {
    if (!(event instanceof BeamNonprofitSelectEvent)) return; // TypeScript guard
    const { selectedNonprofitId } = event.detail;
    this.nonprofitId = selectedNonprofitId ?? null;
    this.activeWidget = "subscription-impact";
  }

  isNonprofitEligible(nonprofitId: TNumericId) {
    return this.eligibleNonprofits.some((np: TIAPIV3EligibleNonprofits[0]) => np.nonprofit.id === nonprofitId);
  }

  shouldDisplayProgressBar(progressValue: number) {
    return progressValue > MIN_PROGRESS_THRESHOLD && progressValue < MAX_PROGRESS_THRESHOLD;
  }

  public get cssVariables() {
    const defaults = {
      ...partnerLogosConfigDefaults,
      ...progressBarConfigDefaults,
      "--beam-fontFamily": "inherit",
      "--beam-fontStyle": "inherit",
      "--beam-fontSize": "inherit",
      "--beam-textColor": "inherit",
      "--beam-backgroundColor": "inherit",
      "--beam-SubscriptionManagement-widgetContainer-borderColor": "#d5d5d5",
      "--beam-SubscriptionManagement-widgetContainer-borderRadius": "4px",
      "--beam-SubscriptionManagement-widgetContainer-padding": "8px 20px",
      "--beam-SubscriptionManagement-widgetContainer-backgroundColor": "inherit",
      "--beam-SubscriptionManagement-widgetContainer-maxWidth": "820px",
      "--beam-SubscriptionManagement-logo-align-desktop": "left",
      "--beam-SubscriptionManagement-logo-align-mobile": "center",
      "--beam-SubscriptionManagement-previousImpact-cardStyle": "'selected_icon'",
      "--beam-SubscriptionManagement-header-padding": "10px 20px 10px 0px",
      "--beam-SubscriptionManagement-header-borderRadius": "0px",
      "--beam-SubscriptionManagement-header-backgroundColor": "#fff",
      "--beam-SubscriptionManagement-header-meatballsMenu-fontSize": "24px",
      "--beam-SubscriptionManagement-header-meatballsMenu-color": "24px",
      "--beam-SubscriptionManagement-lineBreak-color": "#ddd",
      "--beam-SubscriptionManagement-lineBreak-width": "1px",
      "--beam-SubscriptionManagement-lineBreak-margin": "10px 0 20px 0",
      "--beam-SubscriptionManagement-tile-marginBottom": "20px",
      "--beam-SubscriptionManagement-tile-border-width": "0px",
      "--beam-SubscriptionManagement-tile-border-color": "#ddd",
      "--beam-SubscriptionManagement-tile-padding": "12.5px",
      "--beam-SubscriptionManagement-tile-borderRadius": "8px",
      "--beam-SubscriptionManagement-tile-background": "#f8fbfb",
      "--beam-SubscriptionManagement-tileContent-gap": " 15px",
      "--beam-SubscriptionManagement-tileContent-padding": "5px 0px",
      "--beam-SubscriptionManagement-tile-icon-borderRadius": "4px",
      "--beam-SubscriptionManagement-tile-icon-height": "28px",
      "--beam-SubscriptionManagement-tile-icon-background-borderRadius": "50%",
      "--beam-SubscriptionManagement-tile-icon-background-width": "50px",
      "--beam-SubscriptionManagement-tile-icon-background-height": "50px",
      "--beam-SubscriptionManagement-tile-icon-background-backgroundColor": "#0a323c",
      "--beam-SubscriptionManagement-tile-icon-background-padding": "10px",
      "--beam-SubscriptionManagement-blockTileDescription-gap": "5px",
      "--beam-SubscriptionManagement-blockTileDescription-padding": "0px",
      "--beam-SubscriptionManagement-blockTileDescription-lineHeight": "130%",
      "--beam-SubscriptionManagement-blockTileDescription-progressBar-width-desktop": "60%",
      "--beam-SubscriptionManagement-blockTileDescription-progressBar-width-mobile": "100%",
      "--beam-SubscriptionManagement-subWidgetContainer-padding": "0px 0px 10px",
      "--beam-SubscriptionManagement-previousImpactContainer-padding-desktop": "10px 24px 10px 0px",
      "--beam-SubscriptionManagement-previousImpactContainer-padding-mobile": "0px",
      "--beam-SubscriptionManagement-dropDown-padding": "15px 8px",
      "--beam-SubscriptionManagement-dropDown-borderRadius": "8px",
      "--beam-SubscriptionManagement-dropDown-backgroundColor": "#f8fbfb",
      "--beam-SubscriptionManagement-dropDown-boxShadow": "0px 4px 28px 0px rgba(0, 0, 0, 0.04)",
      "--beam-SubscriptionManagement-dropDown-width": "128px",
      "--beam-SubscriptionManagement-dropDown-hover-backgroundColor": "#E1F1EE",
      "--beam-SubscriptionManagement-dropDownOptions-textDecoration": "underline",
      ...defineCustomText("--beam-SubscriptionManagement-dropDownOptions", {
        fontSize: "12px",
        fontWeight: "400",
        textTransform: "capitalize",
        fontStyle: "normal",
        color: "#0a323c",
      }),
      "--beam-SubscriptionManagement-yourPreviousImpactTitle-marginBottom": "15px",
      ...defineCustomText("--beam-SubscriptionManagement-yourPreviousImpactTitle", {
        fontSize: "18px",
        fontWeight: "bold",
        lineHeight: "120%",
        textTransform: "capitalize",
        fontStyle: "normal",
        color: "#0a323c",
      }),
      "--beam-SubscriptionManagement-yourPreviousImpact-showAllButton-textDecoration": "underline",
      ...defineCustomText("--beam-SubscriptionManagement-yourPreviousImpact-showAllButton", {
        fontSize: "12px",
        fontWeight: "bold",
        lineHeight: "120%",
        textTransform: "capitalize",
        fontStyle: "normal",
        color: "#0a323c",
      }),
      "--beam-SubscriptionManagement-tile-title-letterSpacing": "0.36px",
      ...defineCustomText("--beam-SubscriptionManagement-tile-title", {
        fontSize: "12px",
        fontWeight: "bold",
        lineHeight: "120%",
        textTransform: "uppercase",
        fontStyle: "normal",
        color: "#0a323c",
      }),
      ...defineCustomText("--beam-SubscriptionManagement-tile-description", {
        fontSize: "14px",
        lineHeight: "120%",
        fontStyle: "normal",
        color: "#454a47",
      }),
      ...defineCustomText("--beam-SubscriptionManagement-tile-via", {
        fontSize: "14px",
        lineHeight: "120%",
        fontStyle: "normal",
        color: "#454a47",
      }),
      "--beam-SubscriptionManagement-tile-nonprofitLink-textDecoration": "underline",
      ...defineCustomText("--beam-SubscriptionManagement-tile-nonprofitLink", {
        fontSize: "14px",
        fontWeight: "400",
        lineHeight: "120%",
        textTransform: "capitalize",
        fontStyle: "normal",
        color: "#0a323c",
      }),
      "--beam-SubscriptionManagement-tile-supportAgainButton-textDecoration": "underline",
      ...defineCustomText("--beam-SubscriptionManagement-tile-supportAgainButton", {
        fontSize: "14px",
        fontWeight: "400",
        lineHeight: "120%",
        textTransform: "capitalize",
        fontStyle: "normal",
        color: "#0a323c",
      }),
      "--beam-SubscriptionManagement-tile-progressBarLabel-marginLeft": "8px",
      ...defineCustomText("--beam-SubscriptionManagement-tile-progressBarLabel", {
        fontSize: "10px",
        fontWeight: "bold",
        fontStyle: "normal",
        color: "#878a92",
      }),
      "--beam-SubscriptionManagement-tile-fundedAmount-lineHeight": "16px",
      ...defineCustomText("--beam-SubscriptionManagement-tile-fundedAmount", {
        fontSize: "14px",
        fontWeight: "400",
        lineHeight: "120%",
        fontStyle: "italic",
        color: "#0a323c",
      }),
    };

    const remoteConfig = this.eligibleNonprofitsDataController?.data?.config?.web?.theme || {};

    const config = { ...defaults, ...remoteConfig };

    const serializable = Object.create({
      toCSS() {
        return _cssVariableMap(this as Record<string, string>);
      },
    });
    return Object.assign(serializable, config);
  }

  static styles = [
    cssReset,
    css`
      :host {
        display: block;
        font-family: var(--beam-fontFamily);
        font-style: var(--beam-fontStyle);
        font-size: var(--beam-fontSize);
        background-color: var(--beam-backgroundColor);
        color: var(--beam-textColor);
        word-break: normal;
      }
      .drop-down-options {
        text-decoration: var(--beam-SubscriptionManagement-dropDownOptions-textDecoration);
        ${useCustomText("--beam-SubscriptionManagement-dropDownOptions")};
      }
      .widget-container {
        background-color: var(--beam-SubscriptionManagement-widgetContainer-backgroundColor);
        border: 1px solid var(--beam-SubscriptionManagement-widgetContainer-borderColor);
        border-radius: var(--beam-SubscriptionManagement-widgetContainer-borderRadius);
        padding: var(--beam-SubscriptionManagement-widgetContainer-padding);
        max-width: var(--beam-SubscriptionManagement-widgetContainer-maxWidth);
      }
      .header {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        padding: var(--beam-SubscriptionManagement-header-padding);
        border-radius: var(--beam-SubscriptionManagement-header-borderRadius);
        background-color: var(--beam-SubscriptionManagement-header-backgroundColor);
      }
      .header-content {
        display: flex;
        justify-content: space-between;
        width: 100%;
      }
      .button {
        cursor: pointer;
        background: none;
        border: none;
      }
      .header .meatballsMenu {
        position: relative;
        cursor: pointer;
      }
      .header .meatballsMenu span {
        font-size: var(--beam-SubscriptionManagement-header-meatballsMenu-fontSize);
        color: var(--beam-SubscriptionManagement-header-meatballsMenu-color);
      }
      .dropdown-container {
        display: none;
        position: absolute;
        right: 0;
        z-index: 1;
        padding: var(--beam-SubscriptionManagement-dropDown-padding);
        border-radius: var(--beam-SubscriptionManagement-dropDown-borderRadius);
        background: var(--beam-SubscriptionManagement-dropDown-backgroundColor);
        box-shadow: var(--beam-SubscriptionManagement-dropDown-boxShadow);
        width: var(--beam-SubscriptionManagement-dropDown-width);
      }
      .dropdown-container.visible {
        display: block;
      }
      .dropdown-container:hover {
        background-color: var(--beam-SubscriptionManagement-dropDown-hover-backgroundColor);
      }
      .line-break {
        border-top: var(--beam-SubscriptionManagement-lineBreak-width) solid
          var(--beam-SubscriptionManagement-lineBreak-color);
        margin: var(--beam-SubscriptionManagement-lineBreak-margin);
      }
      .tile {
        border: var(--beam-SubscriptionManagement-tile-border-width) solid
          var(--beam-SubscriptionManagement-tile-border-color);
        padding: var(--beam-SubscriptionManagement-tile-padding);
        border-radius: var(--beam-SubscriptionManagement-tile-borderRadius);
        background: var(--beam-SubscriptionManagement-tile-background);
      }
      .tile-title {
        letter-spacing: var(--beam-SubscriptionManagement-tile-title-letterSpacing);
        ${useCustomText("--beam-SubscriptionManagement-tile-title")};
      }
      .tile-content {
        display: flex;
        flex-direction: row;
        gap: var(--beam-SubscriptionManagement-tileContent-gap);
        padding: var(--beam-SubscriptionManagement-tileContent-padding);
      }
      .tile-description-block-container {
        flex: 1 1 67%;
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: var(--beam-SubscriptionManagement-blockTileDescription-gap);
        padding: var(--beam-SubscriptionManagement-blockTileDescription-padding);
        line-height: var(--beam-SubscriptionManagement-blockTileDescription-lineHeight);
      }
      .sub-widget-container {
        padding: var(--beam-SubscriptionManagement-subWidgetContainer-padding);
      }
      .previous-impact-title {
        margin-bottom: var(--beam-SubscriptionManagement-yourPreviousImpactTitle-marginBottom);
        ${useCustomText("--beam-SubscriptionManagement-yourPreviousImpactTitle")};
      }
      .tile-description {
        ${useCustomText("--beam-SubscriptionManagement-tile-description")};
      }
      .via {
        ${useCustomText("--beam-SubscriptionManagement-tile-via")};
      }
      .center-align-logo {
        margin-left: auto;
        margin-right: auto;
      }
      .left-align-logo {
        margin-left: 0;
        margin-right: auto;
      }
      .previous-impact-link-container {
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .previous-impact-link {
        text-decoration-line: var(--beam-SubscriptionManagement-yourPreviousImpact-showAllButton-textDecoration);
        ${useCustomText("--beam-SubscriptionManagement-yourPreviousImpact-showAllButton")};
      }
      .icon-background {
        width: var(--beam-SubscriptionManagement-tile-icon-background-width);
        height: var(--beam-SubscriptionManagement-tile-icon-background-height);
        border-radius: var(--beam-SubscriptionManagement-tile-icon-background-borderRadius);
        padding: var(--beam-SubscriptionManagement-tile-icon-background-padding);
        overflow: hidden;
        background: var(--beam-SubscriptionManagement-tile-icon-background-backgroundColor);
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .icon {
        display: block;
        margin: auto;
        border-radius: var(--beam-SubscriptionManagement-tile-icon-borderRadius);
        height: var(--beam-SubscriptionManagement-tile-icon-height);
      }
      .tile-progress-bar-container-desktop {
        display: flex;
        align-items: center;
        width: var(--beam-SubscriptionManagement-blockTileDescription-progressBar-width-desktop);
      }
      .tile-progress-bar-container-mobile {
        display: flex;
        align-items: center;
        width: var(--beam-SubscriptionManagement-blockTileDescription-progressBar-width-mobile);
      }
      .previous-impact-nonprofit-link {
        text-decoration-line: var(--beam-SubscriptionManagement-tile-nonprofitLink-textDecoration);
        ${useCustomText("--beam-SubscriptionManagement-tile-nonprofitLink")};
      }
      .details-funding-progress-label {
        white-space: nowrap;
        flex: 0 1;
        margin-left: var(--beam-SubscriptionManagement-tile-progressBarLabel-marginLeft);
        ${useCustomText("--beam-SubscriptionManagement-tile-progressBarLabel")};
      }
      .funded-times {
        line-height: var(--beam-SubscriptionManagement-tile-fundedAmount-lineHeight);
        ${useCustomText("--beam-SubscriptionManagement-tile-fundedAmount")};
      }
      .tile-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .tile-support-again {
        text-decoration-line: var(--beam-SubscriptionManagement-tile-supportAgainButton-textDecoration);
        ${useCustomText("--beam-SubscriptionManagement-tile-supportAgainButton")};
      }
    `,
    cssCardGrid({
      gap: "var(--beam-SubscriptionManagement-tile-marginBottom)",
      itemMinWidth: "250px",
      columnCount: "1",
    }),
  ];

  private renderPreviousImpactTiles({
    personalImpact,
    cardStyle,
    eligibleNonprofitConfig,
  }: {
    personalImpact?: TIAPIV3PersonalImpact;
    cardStyle: string;
    eligibleNonprofitConfig?: TIAPIV3EligibleNonprofitConfig;
  }) {
    if (!personalImpact) {
      return html``;
    }
    if (!eligibleNonprofitConfig) {
      return html``;
    }

    const tilesToShow = this.showAllTiles ? personalImpact : personalImpact.slice(0, INITIAL_TILE_COUNT);
    let emojiIndex = 0;
    return html`
      ${tilesToShow.map((previousImpact) => {
        const {
          impact: { goalCompletionText, description, goalProgressPercentage, goalProgressText },
          nonprofit: { cause, causeIconSelectedUrl, causeIconUrl, website, name, id },
        } = previousImpact;

        const isEligibleToSupportAgain = this.nonprofitId !== id && this.isNonprofitEligible(id);
        const showProgressBar = isEligibleToSupportAgain && this.shouldDisplayProgressBar(goalProgressPercentage);
        const emoji = goalCompletionText ? `${this.emojis[emojiIndex]} ` : "";
        emojiIndex = (emojiIndex + 1) % this.emojis.length;

        return html`
          <div class="tile">
            <div class="tile-header">
              <div class="tile-title">${localizeUserString(this.configLang, cause || "")}</div>
              ${isEligibleToSupportAgain
                ? html` <button
                    class="tile-support-again button"
                    @click="${() => this.handleSupportAgainButtonClick(id, name)}"
                  >
                    ${localizeUserString(
                      this.configLang,
                      eligibleNonprofitConfig?.web?.additionalStrings?.["supportAgainButton"] || ""
                    ) || strings[this.configLang].supportAgainButton()}
                  </button>`
                : html``}
            </div>
            <div class="tile-content">
              <div class="icon-background">
                <img
                  src="${cardStyle === "'selected_icon'" ? causeIconSelectedUrl : causeIconUrl}"
                  alt="${cause}"
                  class="icon"
                />
              </div>
              <div class="tile-description-block-container">
                <div class="tile-description-container">
                  <span class="tile-description">${localizeUserString(this.configLang, description || "")}</span>
                  <span class="via"
                    >${localizeUserString(
                      this.configLang,
                      eligibleNonprofitConfig?.web?.additionalStrings?.["via"] || ""
                    ) || strings[this.configLang].via()}</span
                  >
                  <a href="${website}" target="_blank" class="previous-impact-nonprofit-link">
                    ${localizeUserString(this.configLang, name || "")}
                  </a>
                </div>
                ${showProgressBar
                  ? html` <div
                      class="${this.isMobile
                        ? "tile-progress-bar-container-mobile"
                        : "tile-progress-bar-container-desktop"}"
                    >
                      <beam-progress-bar value="${goalProgressPercentage}" style="flex: 1 0;"></beam-progress-bar>
                      <span class="details-funding-progress-label">${goalProgressText}</span>
                    </div>`
                  : html``}
                <div class="funded-times">
                  ${emoji}${unsafeHTML(localizeUserString(this.configLang, goalCompletionText))}
                </div>
              </div>
            </div>
          </div>
        `;
      })}
    `;
  }

  render() {
    const { data, loading, error } = this.eligibleNonprofitsDataController;
    if (loading) {
      return _loading();
    }
    const excludeDonationsForRecurringTransactions = data?.settings?.excludeDonationsForRecurringTransactions;
    if (excludeDonationsForRecurringTransactions) {
      return html``;
    }
    if (error || data == null) {
      return this.debug ? _errorMessage({ error: error || new Error("No data") }) : "";
    }
    let content;
    const cardStyle = this.cssVariables["--beam-SubscriptionManagement-previousImpact-cardStyle"];
    const logoAlignDesktop = this.cssVariables["--beam-SubscriptionManagement-logo-align-desktop"];
    const logoAlignMobile = this.cssVariables["--beam-SubscriptionManagement-logo-align-mobile"];
    const previousImpactCount = this.getDisplayablePreviousImpacts().length ?? 0;
    if (
      this.subscriptionDataController.loading ||
      this.eligibleNonprofitsDataController.loading ||
      this.personalImpactDataController.loading
    ) {
      _loading();
    }

    if (
      (this.subscriptionDataController.error ||
        this.eligibleNonprofitsDataController.error ||
        this.personalImpactDataController.error) &&
      this.debug
    ) {
      if (this.subscriptionDataController.error !== null) {
        return _errorMessage({ error: this.subscriptionDataController.error });
      } else if (this.eligibleNonprofitsDataController.error !== null) {
        return _errorMessage({ error: this.eligibleNonprofitsDataController.error });
      } else if (this.personalImpactDataController.error !== null) {
        return _errorMessage({ error: this.personalImpactDataController.error });
      }
    } else {
      content = "";
    }

    if (this.activeWidget === "select-subscription-nonprofit") {
      content = html` <beam-select-subscription-nonprofit
        part="subscription-nonprofit"
        storeId="${this.storeId}"
        subscriptionId="${this.subscriptionId}"
        nonprofitId=${this.nonprofitId}
        selectedNonprofitId=${this.selectedNonprofitId}
        baseUrl="${this.baseUrl}"
        apiKey="${this.apiKey}"
        postalCode="${this.postalCode}"
        countryCode="${this.countryCode}"
        lang="${this.lang}"
        ?draftConfig="${this.draftConfig}"
        ?debug="${this.debug}"
        @beamnonprofitselect="${(event: Event) => this.handleNonprofitSelect(event)}"
      ></beam-select-subscription-nonprofit>`;
    } else if (this.activeWidget === "subscription-impact") {
      content = html`
        <beam-subscription-impact
          part="subscription-impact"
          baseUrl="${this.baseUrl}"
          apiKey="${this.apiKey}"
          storeId="${this.storeId}"
          beamUserId="${this.beamUserId}"
          nonprofitId="${this.nonprofitId}"
          lang="${this.lang}"
          ?draftConfig="${this.draftConfig}"
          ?debug="${this.debug}"
        ></beam-subscription-impact>
      `;
    }
    return html`
      <style>
        :host {
          ${this.cssVariables.toCSS()}
        }
      </style>
      <div class="widget-container">
        <div class="header">
          <div class="header-content">
            <div
              class="logo ${this.isMobile
                ? logoAlignMobile === "center"
                  ? "center-align-logo"
                  : "left-align-logo"
                : logoAlignDesktop === "center"
                ? "center-align-logo"
                : "left-align-logo"}"
            >
              <beam-partner-logos
                partnerName="${this.eligibleNonprofitsDataController.data?.chain.name}"
                partnerLogoUrl="${this.eligibleNonprofitsDataController.data?.chain.logoUrl}"
              ></beam-partner-logos>
            </div>
            ${this.nonprofitId
              ? html` <div class="meatballsMenu" @click="${() => (this.dropdownVisible = !this.dropdownVisible)}">
                  <span>&#8230;</span>
                  <div class="dropdown-container ${this.dropdownVisible ? "visible" : ""}">
                    ${this.activeWidget === "select-subscription-nonprofit"
                      ? html`<button
                          @click="${this.handleBackToSubscriptionImpactButtonClick}"
                          class="drop-down-options button"
                        >
                          ${localizeUserString(
                            this.configLang,
                            data?.config?.web?.additionalStrings?.["previousNonprofitButtonText"] || ""
                          ) || strings[this.configLang].previousNonprofitButton()}
                        </button>`
                      : this.activeWidget === "subscription-impact"
                      ? html`<button @click="${this.handleSwapNonprofitButtonClick}" class="drop-down-options button">
                          ${localizeUserString(
                            this.configLang,
                            data?.config?.web?.additionalStrings?.["changeNonprofitButtonText"] || ""
                          ) || strings[this.configLang].changeNonprofitButton()}
                        </button>`
                      : ""}
                  </div>
                </div>`
              : html``}
          </div>
        </div>
        <div class="sub-widget-container" part="previous-impact">${content}</div>
        ${previousImpactCount > MIN_PREVIOUS_IMPACT
          ? html` <div class="line-break"></div>

              <div
                class="previous-impact-container ${this.isMobile
                  ? "--beam-SubscriptionManagement-previousImpactContainer-padding-mobile"
                  : "--beam-SubscriptionManagement-previousImpactContainer-padding-desktop"}"
              >
                <h2 class="previous-impact-title">
                  ${localizeUserString(
                    this.configLang,
                    data?.config?.web?.additionalStrings?.["previousImpactHeader"] || ""
                  ) || strings[this.configLang].previousImpactHeader()}
                </h2>
                <div class="cardGrid">
                  ${this.renderPreviousImpactTiles({
                    personalImpact: this.getDisplayablePreviousImpacts(),
                    cardStyle,
                    eligibleNonprofitConfig: this.eligibleNonprofitsDataController.data?.config,
                  })}
                </div>
                ${previousImpactCount > INITIAL_TILE_COUNT
                  ? html`
                      <div class="previous-impact-link-container">
                        <button
                          @click="${() => (this.showAllTiles = !this.showAllTiles)}"
                          class="previous-impact-link button"
                        >
                          ${this.showAllTiles
                            ? localizeUserString(
                                this.configLang,
                                data?.config?.web?.additionalStrings?.["showLessButton"] || ""
                              ) || strings[this.configLang].showLessButton()
                            : localizeUserString(
                                this.configLang,
                                data?.config?.web?.additionalStrings?.["showAllButton"] || ""
                              ) || strings[this.configLang].showAllButton()}
                        </button>
                      </div>
                    `
                  : html``}
              </div>`
          : html``}
      </div>
    `;
  }
}

defineCustomElement(BeamSubscriptionManagement);

declare global {
  interface HTMLElementTagNameMap {
    "beam-subscription-management": BeamSubscriptionManagement;
  }
}
